/* VEYLA | add-to-cart upsell
   ---------------------------------------------------------------------------
   One question, asked once, in the gap between the buybox and the cart.

   The buybox owns the add. This file owns nothing but the question: it never
   posts to the cart, never opens the drawer and never touches the ladder. It
   hands back the EXTRA line items the shopper agreed to, and vy-pdp.js adds
   them alongside the serum in the same call.

       window.VYUpsell.ask()   -> Promise<Array>   [] when she declines
       window.VYUpsell.reset() -> harness only, re-arms the one-shot

   Why it resolves rather than rejects on a no: declining is not an error. She
   clicked add to cart, so the serum lands either way. The X, the scrim and
   Escape all mean the same thing as "No thanks", because a shopper who has
   already asked to buy must never lose the thing she asked for by closing a
   window we opened over it.

   Asked AFTER the payload is built (see vy-pdp.js), so a broken ladder never
   gets as far as offering an add-on for an order that cannot be placed. */
(() => {
  'use strict';

  /* Read off /products/growth-shampoo.js 2026-08-16. The price lives here for
     DISPLAY only: what the cart charges is whatever Shopify has on the variant,
     and the drawer reads it back from its own catalogue. Two places to change a
     price is one too many, so if this product ever moves, move it in
     vy-drawer.js CATALOG as well. */
  const SHAMPOO = { id: 50884911005863, cents: 1995, was: 3995 };

  /* Ten minutes, held per VISIT rather than per opening of the card. The
     deadline is stamped once and parked in sessionStorage, so a reload does not
     hand her a fresh ten minutes: a countdown that resets itself is the thing
     that makes shoppers stop believing countdowns. At zero the clock stops and
     the line changes, and that is all it does. The offer still adds at the same
     price, because refusing a purchase over a timer we started is not something
     this page is willing to do. */
  const HOLD_MS = 10 * 60 * 1000;
  const HOLD_KEY = 'vy-ups-hold';

  const doc  = document;
  const root = doc.getElementById('vy-ups');

  /* No markup, no question. The page must still add to cart. */
  if (!root) { window.VYUpsell = { ask: () => Promise.resolve([]), reset() {} }; return; }

  const $u = s => root.querySelector(s);
  const now  = $u('#vy-ups-now');
  const was  = $u('#vy-ups-was');
  const yes  = $u('#vy-ups-yes');
  const yesP = $u('#vy-ups-yes-price');
  const save = $u('#vy-ups-save');
  const hold = $u('#vy-ups-timer');
  const clock = $u('#vy-ups-clock');
  const holdTxt = $u('#vy-ups-timer-txt');

  /* Never format a price here. vy-offer.js routes money() through the store's
     own currency converter, which is the only thing that knows what a shopper
     in Casablanca is being shown. The fallback exists for the prototype, where
     there is no offer module loaded yet. */
  const money = c => (window.VY_OFFER ? window.VY_OFFER.money(c) : '$' + (c / 100).toFixed(2));

  let answered = false;   // one question per visit
  let resolve  = null;    // live while the card is open

  function paint() {
    now.textContent  = money(SHAMPOO.cents);
    was.textContent  = money(SHAMPOO.was);
    yesP.textContent = money(SHAMPOO.cents);
    /* The saving is SUBTRACTED, not typed into the markup: one number moves and
       the pill follows it, in whatever currency the store is showing her. */
    if (save) save.textContent = 'Save ' + money(SHAMPOO.was - SHAMPOO.cents);
  }
  /* The converter answers a beat after it is asked, so a price rendered in the
     same frame the card opens can arrive in dollars and correct itself. */
  doc.addEventListener('vy:currency', () => { if (!root.hidden) paint(); });

  const inCart = () => (window.__VY_CART || []).some(l => l.id === SHAMPOO.id);

  /* ---------- the hold clock ----------
     sessionStorage rather than localStorage: the hold belongs to this visit.
     Wrapped because Safari private mode throws on it, and a storage error must
     never be the reason an add-to-cart question fails to open. */
  const store = {
    get() { try { return +sessionStorage.getItem(HOLD_KEY) || 0; } catch (e) { return 0; } },
    set(v) { try { sessionStorage.setItem(HOLD_KEY, String(v)); } catch (e) { /* ignore */ } }
  };

  let ticker = null;

  const mmss = ms => {
    const s = Math.max(0, Math.round(ms / 1000));
    return Math.floor(s / 60) + ':' + String(s % 60).padStart(2, '0');
  };

  function tick() {
    const left = store.get() - Date.now();
    if (left > 0) { clock.textContent = mmss(left); return; }
    /* out of time: the clock stops, the offer does not */
    clock.textContent = '0:00';
    holdTxt.textContent = 'Last chance at this price';
    hold.classList.add('is-out');
    stopClock();
  }

  function stopClock() {
    if (ticker) { clearInterval(ticker); ticker = null; }
  }

  function startClock() {
    if (!hold) return;
    if (!store.get()) store.set(Date.now() + HOLD_MS);
    tick();
    stopClock();
    /* only while the card is open: a page-long interval behind a closed dialog
       is a timer nobody is reading */
    ticker = setInterval(tick, 1000);
  }

  function close(items) {
    stopClock();
    root.hidden = true;
    doc.body.classList.remove('vy-noscroll');
    const done = resolve;
    resolve = null;
    if (done) done(items);
  }

  function ask() {
    /* Already answered, or already hers: adding it twice is not an upsell. */
    if (answered || inCart()) return Promise.resolve([]);
    answered = true;
    paint();
    startClock();
    root.hidden = false;
    doc.body.classList.add('vy-noscroll');
    if (yes.focus) yes.focus();
    return new Promise(r => { resolve = r; });
  }

  yes.addEventListener('click', () => close([{ id: SHAMPOO.id, quantity: 1 }]));
  root.querySelectorAll('[data-ups-no]').forEach(b =>
    b.addEventListener('click', () => close([])));
  doc.addEventListener('keydown', e => {
    if (e.key === 'Escape' && !root.hidden) close([]);
  });

  /* reset() is for the harness. Nothing on the page calls it: a visitor is
     asked once. It puts the clock line back the way the markup ships it so a
     second open in a test run is not reading the last one's expired state. */
  window.VYUpsell = {
    ask,
    reset(keepHold) {
      answered = false;
      if (!keepHold) store.set(0);
      if (hold) {
        hold.classList.remove('is-out');
        holdTxt.textContent = 'Discount expires in';
        clock.textContent = mmss(HOLD_MS);
      }
    }
  };
})();
